import { ChangeEvent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Error } from '@components/error/src/error';
import { IconChevron } from '@components/icons';
import { Input } from '@components/input';
import { SwitchToggle } from '@components/switch-toggle';
import classNames from 'classnames';

import { useTeamChannelErrorMessage } from './team-channel-section-context';
import { ValidationError } from './validators';

interface RowElementProps {
  rowId: string;
  name: string;
  isMobileViewPort: boolean;
  isSalesClosed: boolean;
  maxTicketQuantity: number | null;
  soldItems?: number | null;
  showChevron?: boolean;
  isChannelsVisible?: boolean;
  errorStyles: string;
  error?: ValidationError;
  onMaxQuantityChange: (next: number | null) => void;
  onToggleSalesClosed: (isClosed: boolean) => void;
  onToggleChevron?: () => void;
  // When false the per-row "close sales" toggle is hidden (admission-form parity); the quantity box
  // is the only control. Defaults to true so the event-capacity modal keeps the toggle.
  canCloseSales?: boolean;
  // Typography for the row label. Defaults to the bold subtitle (event-capacity modal); the admission
  // form overrides it with its lighter figma token. Consumer-supplied so styling stays out of the atom.
  labelClassName?: string;
  className?: string;
}

const parseInput = (raw: string): number | null => {
  if (raw === '') return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return n;
};

export const RowElement = memo<RowElementProps>(
  ({
    rowId,
    name,
    isMobileViewPort,
    isSalesClosed,
    maxTicketQuantity,
    soldItems,
    showChevron = false,
    isChannelsVisible,
    errorStyles,
    error,
    onMaxQuantityChange,
    onToggleSalesClosed,
    onToggleChevron,
    canCloseSales = true,
    labelClassName = 'text-subtitle-s sm:text-subtitle-m',
    className,
  }) => {
    const { t } = useTranslation();
    const errorMessage = useTeamChannelErrorMessage(error);
    const inputValue = useMemo(
      () => (maxTicketQuantity === null ? '' : String(maxTicketQuantity)),
      [maxTicketQuantity]
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onMaxQuantityChange(parseInput(e.currentTarget.value));
    };

    return (
      <>
        <div className={classNames('flex justify-between items-center', className)}>
          <div
            className={classNames(
              { 'gap-0': isMobileViewPort, 'gap-4': !isMobileViewPort },
              'flex gap-4 items-center'
            )}>
            {showChevron && onToggleChevron && (
              <button type='button' onClick={onToggleChevron}>
                <IconChevron
                  className={classNames(' w-3 h-3 stroke-2 transition duration-200 ease-in-out', {
                    '-rotate-90': isChannelsVisible,
                    '-rotate-180': !isChannelsVisible,
                    'ml-2': !isMobileViewPort,
                  })}
                />
              </button>
            )}
            <div
              className={classNames({
                'w-20': !isMobileViewPort,
                'w-14 ml-4': isMobileViewPort,
              })}>
              <Input
                hasError={!!error}
                type='number'
                placeholder='∞'
                disabled={isSalesClosed}
                step={1}
                label={name}
                min={1}
                value={inputValue}
                onChange={handleChange}
              />
            </div>
            <div className={classNames('flex items-start ml-3 flex-col')}>
              <div className='flex content-between'>
                <label
                  className={classNames(
                    {
                      'text-secondary': isSalesClosed,
                      'text-primary': !isSalesClosed,
                    },
                    labelClassName,
                    'text-ellipsis'
                  )}
                  htmlFor={rowId}>
                  {name}
                </label>
              </div>
              {!!soldItems && (
                <p
                  className={classNames({
                    'text-tertiary': isSalesClosed,
                    'text-secondary': !isSalesClosed,
                    'text-body-s': !isMobileViewPort,
                    'text-body-xs': isMobileViewPort,
                  })}>
                  {t('offers:channelTicketsSold')} {soldItems}
                </p>
              )}
            </div>
          </div>
          {canCloseSales && (
            <SwitchToggle
              active={isSalesClosed}
              loading={false}
              onToggleSwitch={onToggleSalesClosed}
              reverseOperation={true}
            />
          )}
        </div>
        {errorMessage && (
          <div className={errorStyles}>
            <Error>{errorMessage}</Error>
          </div>
        )}
      </>
    );
  }
);

RowElement.displayName = 'RowElement';
